import type { ReactNode } from 'react';
import { FeatherIcon } from './FeatherIcon';
import { C, FONT } from './ChatAssistant/compact/tokens';
import './ChatAssistant/compact/compact.css';

/* Shared page chrome for the top-level pages (Dashboard, Statistics, Payments, Agency)
   and the account page, so they all use one title size and one tab style. */

/** Page title (18px bold) with an optional one-line subtitle (12px). */
export function PageHeading({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <h1 style={{ margin: 0, font: `700 18px/1.2 ${FONT}`, color: C.text, overflowWrap: 'anywhere' }}>{title}</h1>
      {subtitle && <span style={{ font: `400 12px ${FONT}`, color: C.sub }}>{subtitle}</span>}
    </div>
  );
}

export interface PageTab {
  id: string;
  label: string;
  icon: string;
  /** Small trailing figure, e.g. a count. */
  badge?: ReactNode;
}

/** Underline tabs — the account page's Chat Assistant / Mass Messages bar. Scrolls sideways when it outgrows the row. */
export function PageTabs({ tabs, active, onChange }: { tabs: PageTab[]; active: string; onChange: (id: string) => void }) {
  return (
    <nav
      className="cpt-noscrollbar"
      style={{ display: 'flex', gap: 4, overflowX: 'auto', boxShadow: `inset 0 -1px 0 ${C.divider}` }}
    >
      {tabs.map((tab) => {
        const on = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            aria-current={on ? 'page' : undefined}
            className="cpt-hover-text"
            style={{
              height: 36, flex: 'none', padding: '0 10px', border: 0, borderBottom: `2px solid ${on ? C.purple : 'transparent'}`,
              background: 'transparent', color: on ? C.text : C.sub, font: `500 13px ${FONT}`, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            <FeatherIcon name={tab.icon} size={14} />
            {tab.label}
            {tab.badge != null && <span style={{ font: `500 11px ${FONT}`, color: C.muted }}>{tab.badge}</span>}
          </button>
        );
      })}
    </nav>
  );
}
